import { TPivotReportConfig } from '@infotech-soe/merps/types/Reports';
import {
  CategoryName,
  DisplayModeName,
  TFormName,
} from '@infotech-soe/metaschema';

import IApiProvider from '../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { IPanelLink } from '../../models/dataset/DatasetRelated';
import DisplayModeConfig from '../../models/user-config/DisplayModeConfig';
import TGeneralUserConfig from '../../models/user-config/GeneralUserConfig';
import { FormConfigFields } from '../form/view-model/FormSource';

import IUserConfigService, {
  DisplayModeConditions,
} from './IUserConfigService';

/**
 * Default implementation of AbstractUserConfigService
 */
class UserConfigServiceSingleton implements IUserConfigService {
  private readonly apiProvider: IApiProvider;
  constructor() {
    this.apiProvider = new MerpsApiProvider();
  }

  public saveConditionsConfig(
    category: CategoryName,
    displayMode: DisplayModeName,
    form: TFormName,
    data: DisplayModeConditions
  ): Promise<void> {
    return this.apiProvider.userConfig.saveConditionsConfig(
      category,
      displayMode,
      form,
      data
    );
  }

  public getConditionsConfig(
    category: CategoryName,
    displayMode: DisplayModeName,
    form: string,
    withDefaultConfig?: boolean
  ): Promise<DisplayModeConditions> {
    return this.apiProvider.userConfig.getConditionsConfig(
      category,
      displayMode,
      form,
      withDefaultConfig
    );
  }

  public saveDisplayModeConfig(
    category: CategoryName,
    displayMode: DisplayModeName,
    data: DisplayModeConfig
  ): Promise<void> {
    return this.apiProvider.userConfig.saveDisplayModeConfig(
      category,
      displayMode,
      data
    );
  }

  public getDisplayModeConfig(
    category: CategoryName,
    displayMode: DisplayModeName,
    withDefaultConfig?: boolean
  ): Promise<DisplayModeConfig> {
    return this.apiProvider.userConfig.getDisplayModeConfig(
      category,
      displayMode,
      withDefaultConfig
    );
  }

  public savePanelLinks(data: IPanelLink[]): Promise<void> {
    return this.apiProvider.userConfig.savePanelLinks(data);
  }

  public getPanelLinks(): Promise<IPanelLink[]> {
    return this.apiProvider.userConfig.getPanelLinks();
  }

  public saveFormConfig(
    category: CategoryName,
    form: string,
    data: Record<string, FormConfigFields>
  ): Promise<void> {
    return this.apiProvider.userConfig.saveFormConfig(category, form, data);
  }

  public async getFormConfig(
    category: CategoryName,
    form: string
  ): Promise<Record<string /* TFormFieldName */, FormConfigFields>> {
    const formConfig = await this.apiProvider.userConfig.getFormConfig(
      category,
      form
    );

    Object.values(formConfig).forEach(field => {
      if (Array.isArray(field.history)) {
        field.history = field.history.map((item: any) => {
          if (typeof item === 'object' && 'value' in item) {
            return item.value;
          }
          return item;
        });
      }
    });

    return formConfig;
  }

  public savePivotConfig(
    category: CategoryName,
    code: string,
    data: TPivotReportConfig
  ): Promise<void> {
    return this.apiProvider.userConfig.savePivotConfig(category, code, data);
  }

  public getPivotConfig(
    category: CategoryName,
    code: string
  ): Promise<TPivotReportConfig | undefined> {
    return this.apiProvider.userConfig.getPivotConfig(category, code);
  }

  public saveGeneralConfig(data: TGeneralUserConfig): Promise<void> {
    return this.apiProvider.userConfig.saveGeneralConfig(data);
  }

  public getGeneralConfig(): Promise<TGeneralUserConfig> {
    return this.apiProvider.userConfig.getGeneralConfig();
  }
}

const UserConfigService = (() => {
  let instance: IUserConfigService;
  return new Proxy(UserConfigServiceSingleton, {
    construct(Target: typeof UserConfigServiceSingleton): IUserConfigService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default UserConfigService;
